import {
  User as AccountEntity,
  ApiEndpoints,
  EntityType,
  ForgotPasswordRequest,
  RegistrationRequest,
  type RegistrationResponse,
  UserRoleAssignmentsByEntity,
} from 'adc-sirius-module-common';
import mediator, { MediatorRequestWithResponse } from '../utilities/mediator';
import { urls } from '../utilities/dataHelpers';
import {
  Account,
  AccountCreationResult,
  AccountRoleAssignmentsUpdateRequest,
  AccountWithRoleAssignments,
} from '../models';
import {
  mapAccountEntityToAccount,
  mapAccountRoleAssignmentsUpdateRequestToUserRoleAssignmentsByEntityUpdate,
  mapAccountToAccountEntity,
  mapUserRoleAssignmentsByEntityToAccountsWithRoleAssignments,
} from '../utilities/mappers';
import { ROUTES } from '../constants';

const handleGetAccounts = (request: MediatorRequestWithResponse<Account[]>) => {
  request.response = new Promise<Account[]>((resolve, reject) => {
    mediator
      .request<AccountEntity[]>('api:invoke', {
        method: ApiEndpoints.EP_USER_GET.Method,
        url: urls.api + ApiEndpoints.EP_USER_GET.Path,
      })
      .then((result) => {
        if (result.status === 200) {
          const accounts = (result.data ?? []).map(mapAccountEntityToAccount);

          resolve(accounts);
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

const handleGetAccount = (request: MediatorRequestWithResponse<Account>) => {
  request.response = new Promise<Account>((resolve, reject) => {
    mediator
      .request<AccountEntity>('api:invoke', {
        method: ApiEndpoints.EP_USER_GET_BY_KEY.Method,
        url: `${urls.api}${ApiEndpoints.EP_USER_GET_BY_KEY.Path.replace(':key', request.key)}`,
      })
      .then((result) => {
        if (result.status === 200) {
          resolve(mapAccountEntityToAccount(result.data));
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

const handleCreateAccount = (
  request: MediatorRequestWithResponse<AccountCreationResult>,
) => {
  const verifyEmailLink = `${window.location.origin}${ROUTES.AUTH.VERIFY_ACCOUNT}`;

  const data: RegistrationRequest = {
    firstName: request.data.firstName,
    lastName: request.data.lastName,
    email: request.data.email,
    password: request.data.password,
    baseUrl: verifyEmailLink,
  };

  request.response = new Promise<AccountCreationResult>((resolve, reject) => {
    mediator
      .request<RegistrationResponse>('api:invoke', {
        method: ApiEndpoints.EP_AUTH_REGISTER.Method,
        url: urls.api + ApiEndpoints.EP_AUTH_REGISTER.Path,
        data,
      })
      .then((result) => {
        if (result.status === 200 && result.data.user) {
          resolve({
            account: mapAccountEntityToAccount(result.data.user),
            verificationRequired: result.data.verificationRequired ?? false,
            verificationEmailSent: result.data.verificationEmailSent ?? false,
          });
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

const handleUpdateAccount = (request: MediatorRequestWithResponse<Account>) => {
  request.response = new Promise<Account>((resolve, reject) => {
    mediator
      .request<AccountEntity>('api:invoke', {
        method: ApiEndpoints.EP_USER_UPDATE.Method,
        url: urls.api + ApiEndpoints.EP_USER_UPDATE.Path,
        data: mapAccountToAccountEntity(request.data),
      })
      .then((result) => {
        if (result.status === 200) {
          resolve(mapAccountEntityToAccount(result.data));
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

const handleResendInvite = (request: MediatorRequestWithResponse<void>) => {
  const verifyEmailLink = `${window.location.origin}${ROUTES.AUTH.VERIFY_ACCOUNT}`;

  request.response = new Promise<void>((resolve, reject) => {
    mediator
      .request<void>('api:invoke', {
        method: ApiEndpoints.EP_AUTH_RESEND_VERIFY.Method,
        url: urls.api + ApiEndpoints.EP_AUTH_RESEND_VERIFY.Path,
        data: { email: request.data, baseUrl: verifyEmailLink },
      })
      .then((result) => {
        if (result.status === 200) {
          resolve();
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

const handleResetPassword = (request: MediatorRequestWithResponse<void>) => {
  request.response = new Promise<void>((resolve, reject) => {
    const email = request.data;

    const data: ForgotPasswordRequest = {
      email,
      source: 'cv',
      baseUrl: `${window.location.origin}${ROUTES.AUTH.RESET_PASSWORD}`,
    };

    mediator
      .request<void>('api:invoke', {
        method: ApiEndpoints.EP_AUTH_FORGOT.Method,
        url: urls.api + ApiEndpoints.EP_AUTH_FORGOT.Path,
        data,
      })
      .then((result) => {
        if (result.status === 200) {
          resolve();
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

const handleAccountLock = (request: MediatorRequestWithResponse<Account>) => {
  request.response = new Promise<Account>((resolve, reject) => {
    mediator
      .request<AccountEntity>('api:invoke', {
        method: 'POST',
        url: `${urls.api}users/${request.key}/lock`,
        data: request.data,
      })
      .then((result) => {
        if (result.status === 200) {
          resolve(mapAccountEntityToAccount(result.data));
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

const handleAccountUnlock = (request: MediatorRequestWithResponse<Account>) => {
  request.response = new Promise<Account>((resolve, reject) => {
    mediator
      .request<AccountEntity>('api:invoke', {
        method: 'POST',
        url: `${urls.api}users/${request.key}/unlock`,
        data: request.data,
      })
      .then((result) => {
        if (result.status === 200) {
          resolve(mapAccountEntityToAccount(result.data));
        } else {
          reject(result);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

const handleGetAccountsByEntity = (
  request: MediatorRequestWithResponse<AccountWithRoleAssignments[]> & {
    key: string;
    entityType: EntityType;
  },
) => {
  const url =
    urls.api +
    ApiEndpoints.EP_USER_GET_BY_ENTITY.Path.replace(
      ':key',
      request.key,
    ).replace(':entityType', request.entityType);

  request.response = new Promise<AccountWithRoleAssignments[]>(
    (resolve, reject) => {
      mediator
        .request<UserRoleAssignmentsByEntity>('api:invoke', {
          method: 'GET',
          url,
          entityType: request.entityType,
        })
        .then((result) => {
          if (result.status === 200) {
            const mappedAccounts =
              mapUserRoleAssignmentsByEntityToAccountsWithRoleAssignments([
                ...(result.data ? [result.data] : []),
              ]);

            resolve(mappedAccounts);
          } else {
            reject(result);
          }
        })
        .catch((err) => {
          reject(err);
        });
    },
  );
};

const handleUpdateAccountRoles = (
  request: MediatorRequestWithResponse<AccountWithRoleAssignments[]> & {
    data: AccountRoleAssignmentsUpdateRequest;
  },
) => {
  const { data } = request;

  const url = urls.api + ApiEndpoints.EP_USER_UPDATE_ROLES.Path;

  request.response = new Promise<AccountWithRoleAssignments[]>(
    (resolve, reject) => {
      mediator
        .request<UserRoleAssignmentsByEntity>('api:invoke', {
          method: ApiEndpoints.EP_USER_UPDATE_ROLES.Method,
          url,
          data: mapAccountRoleAssignmentsUpdateRequestToUserRoleAssignmentsByEntityUpdate(
            data,
          ),
        })
        .then((result) => {
          if (result.status === 200) {
            resolve(
              mapUserRoleAssignmentsByEntityToAccountsWithRoleAssignments([
                ...(result.data ? [result.data] : []),
              ]),
            );
          } else {
            reject(result);
          }
        })
        .catch((err) => {
          reject(err);
        });
    },
  );
};

mediator.subscribe('account:getAccounts', handleGetAccounts);
mediator.subscribe('account:getAccountByKey', handleGetAccount);
mediator.subscribe('account:createAccount', handleCreateAccount);
mediator.subscribe('account:updateAccount', handleUpdateAccount);
mediator.subscribe('account:resendInvite', handleResendInvite);
mediator.subscribe('account:resetPassword', handleResetPassword);
mediator.subscribe('account:lock', handleAccountLock);
mediator.subscribe('account:unlock', handleAccountUnlock);
mediator.subscribe('account:getAccountsByEntity', handleGetAccountsByEntity);
mediator.subscribe('account:updateAccountRoles', handleUpdateAccountRoles);
